import { Pipe, PipeTransform, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { SearchService } from '../services';

/**
 * Transforms code values to human-readable descriptions
 * Uses SearchService cached observables to translate codes
 * 
 * Usage: {{ codeValue | codeDescription:'fieldName' | async }}
 * Example: {{ 'NY' | codeDescription:'policyState' | async }} → 'New York'
 * 
 * Supported field types:
 * - lob, company, routingChannel, partyType, claimStatus
 * - policyState, sourceSystem, inspectionType, templateIndicator
 */
@Pipe({
  name: 'codeDescription',
})
export class CodeDescriptionPipe implements PipeTransform {
  private readonly searchService = inject(SearchService);

  /**
   * Transforms a code value to its description
   * @param code The code value to transform
   * @param fieldName The field type (lob, company, etc.)
   * @returns Observable of description string, or code if not found
   */
  transform(code: string | null | undefined, fieldName: string): Observable<string> {
    if (!code || !fieldName) {
      return of(code || '');
    }

    // Map field names to service methods
    const codeMap$ = this.getCodeMap(fieldName);
    
    if (!codeMap$) {
      // Unknown field type, return code as-is
      return of(code);
    }

    return codeMap$.pipe(
      map(codeMap => codeMap.get(code) || code)
    );
  }

  /**
   * Gets the appropriate code map observable based on field name
   */
  private getCodeMap(fieldName: string): Observable<Map<string, string>> | null {
    switch (fieldName.toLowerCase()) {
      case 'lob':
        return this.searchService.getLobCodes();
      case 'company':
        return this.searchService.getCompanyCodes();
      case 'routingchannel':
        return this.searchService.getRoutingChannelCodes();
      case 'usergroup':
        return this.searchService.getUserGroupCodes();
      case 'partytype':
        return this.searchService.getPartyTypeCodes();
      case 'claimstatus':
      case 'status':
        return this.searchService.getClaimStatusCodes();
      case 'policystate':
      case 'polstate':
        return this.searchService.getPolicyStateCodes();
      case 'sourcesystem':
      case 'source':
        return this.searchService.getSourceSystemCodes();
      case 'inspectiontype':
      case 'insptype':
        return this.searchService.getInspectionTypeCodes();
      case 'templateindicator':
      case 'template':
        return this.searchService.getTemplateIndicatorCodes();
      default:
        return null;
    }
  }
}
